import { useClient } from '@cortex/react-query';
import type { ConsoleFeatureFlags, OrgMember } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { BackButton } from '../../../components/common/BackButton';
import { EmptyState } from '../../../components/common/EmptyState';
import { Modal } from '../../../components/common/Modal';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import { Table, type IColumn } from '../../../components/common/Table';
import { useToast } from '../../../components/common/Toast';
import { readApiValidationErrors } from '../../../lib/api-errors';
import { routes } from '../../../lib/constants';
import {
  getOrgById,
  inviteOrgMemberAdmin,
  listOrgMembersAdmin,
  setOrgEnabledAdmin,
  setOrgMemberEnabledAdmin,
  setOrgMemberRoleAdmin,
} from '../../../lib/orgs';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/organizations/$orgId')({
  beforeLoad: () => guardSuperuser(),
  component: OrgDetailPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

function OrgDetailPage() {
  const { orgId } = Route.useParams();
  const client = useClient();
  const qc = useQueryClient();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);

  const org = useQuery({
    queryKey: ['admin', 'org', orgId],
    queryFn: () => getOrgById(client, orgId),
  });

  const members = useQuery({
    queryKey: ['admin', 'org-members', orgId],
    queryFn: () => listOrgMembersAdmin(client, orgId),
  });

  const toggleOrg = useMutation({
    mutationFn: (enabled: boolean) => setOrgEnabledAdmin(client, orgId, enabled),
    onSuccess: async (_data, enabled) => {
      try {
        await qc.invalidateQueries({ queryKey: ['admin', 'org', orgId] });
        await qc.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      } catch (err) {
        console.error(err);
      }
      toast.show({
        kind: 'success',
        text: enabled ? 'Organization enabled' : 'Organization disabled',
      });
    },
    onError: (err) => {
      toast.show({ kind: 'error', text: `Failed: ${(err as Error).message}` });
    },
  });

  const toggleMember = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; email: string; enabled: boolean }) =>
      setOrgMemberEnabledAdmin(client, orgId, userId, enabled),
    onSuccess: async (_data, vars) => {
      try {
        await qc.invalidateQueries({ queryKey: ['admin', 'org-members', orgId] });
      } catch (err) {
        console.error(err);
      }
      toast.show({
        kind: 'success',
        text: vars.enabled ? `Enabled ${vars.email}` : `Disabled ${vars.email}`,
      });
    },
    onError: (err) => {
      toast.show({ kind: 'error', text: `Failed: ${(err as Error).message}` });
    },
  });

  const changeMemberRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'customer' | 'fde' }) =>
      setOrgMemberRoleAdmin(client, orgId, userId, role),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'org-members', orgId] });
      toast.show({ kind: 'success', text: 'Role updated' });
    },
    onError: (err) => toast.show({ kind: 'error', text: `Failed: ${(err as Error).message}` }),
  });

  if (org.isLoading) return <Spinner />;
  if (org.error)
    return (
      <div className="alert alert--danger">Failed to load: {(org.error as Error).message}</div>
    );
  if (!org.data) return <EmptyState title="Not found" />;

  const cols: IColumn<OrgMember>[] = [
    {
      key: 'email',
      header: 'Email',
      cell: (u) => <span style={{ color: 'var(--text-primary)' }}>{u.email}</span>,
    },
    { key: 'name', header: 'Name', cell: (u) => u.name ?? <span className="t-muted">—</span> },
    {
      key: 'role',
      header: 'Role',
      cell: (u) => (
        <select
          value={u.role}
          aria-label={`Role for ${u.email}`}
          onChange={(e) =>
            changeMemberRole.mutate({ userId: u.id, role: e.target.value as 'customer' | 'fde' })
          }
          disabled={changeMemberRole.isPending}
        >
          <option value="customer">Customer</option>
          <option value="fde">FDE</option>
        </select>
      ),
      width: 140,
    },
    {
      key: 'enabled',
      header: 'Status',
      cell: (u) =>
        u.enabled ? (
          <span className="badge badge--success">Active</span>
        ) : (
          <span className="badge badge--neutral">Disabled</span>
        ),
      width: 120,
    },
    {
      key: 'actions',
      header: '',
      cell: (u) => (
        <button
          type="button"
          className="btn btn--ghost btn--xs"
          onClick={() => {
            const next = !u.enabled;
            const verb = next ? 'Enable' : 'Disable';
            if (confirm(`${verb} ${u.email} in "${org.data?.name ?? ''}"?`))
              toggleMember.mutate({ userId: u.id, email: u.email, enabled: next });
          }}
        >
          {u.enabled ? 'Disable' : 'Enable'}
        </button>
      ),
      width: 120,
    },
  ];

  return (
    <>
      <PageHeader
        title={org.data.name}
        actions={
          <>
            <BackButton to={routes.INTERNAL_ORGS} label="Organizations" />
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => {
                const next = !org.data?.enabled;
                const verb = next ? 'Enable' : 'Disable';
                if (confirm(`${verb} organization "${org.data?.name ?? ''}"?`))
                  toggleOrg.mutate(next);
              }}
              disabled={toggleOrg.isPending}
            >
              {org.data?.enabled ? 'Disable org' : 'Enable org'}
            </button>
          </>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <div className="card">
          <div className="card-header">
            <span className="t-mono-label">METADATA</span>
          </div>
          <div className="card-body">
            <KV label="ID">
              <code className="t-mono">{org.data.id}</code>
            </KV>
            <KV label="Alias">
              <code className="t-mono">{org.data.alias ?? '—'}</code>
            </KV>
            <KV label="Domains">
              {org.data.domains.length > 0
                ? org.data.domains.map((d) => (
                    <span key={d.name} className="badge badge--neutral" style={{ marginRight: 4 }}>
                      {d.name}
                    </span>
                  ))
                : '—'}
            </KV>
          </div>
        </div>

        <FeatureFlagsCard orgId={orgId} />

        <div className="card">
          <div className="card-header">
            <span className="t-mono-label">MEMBERS</span>
            <h3 className="t-h3">Users</h3>
          </div>
          <div className="card-body" style={stack3}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => setInviting(true)}
              >
                Invite user
              </button>
            </div>
            <Table<OrgMember>
              rows={members.data}
              columns={cols}
              rowKey={(u) => u.id}
              isLoading={members.isLoading}
              error={members.error as Error | null}
              emptyTitle="No members yet"
              emptyDescription="Invite the first user to this org by email."
            />
          </div>
        </div>
      </div>

      <InviteModal
        open={inviting}
        onClose={() => setInviting(false)}
        onInvited={async () => {
          try {
            await qc.invalidateQueries({ queryKey: ['admin', 'org-members', orgId] });
          } catch (err) {
            console.error(err);
          }
          setInviting(false);
        }}
        orgId={orgId}
      />
    </>
  );
}

// One row per console feature flag, superuser-only management surface
// (2026-07-07 nav review: flags hide not-yet-customer-ready nav sections).
const flagRows: { key: keyof ConsoleFeatureFlags; label: string; blurb: string }[] = [
  {
    key: 'persistentAgentOrchestration',
    label: 'Persistent Agent Orchestration',
    blurb:
      'Persistent Agent orchestration, self-drive controls, dashboards, activity, and routing.',
  },
  {
    key: 'refinement',
    label: 'Refinement',
    blurb: 'Investigative refinement sessions nav section.',
  },
  {
    key: 'deduplication',
    label: 'Deduplication',
    blurb: 'Signal deduplication settings, analytics, and coordinator views.',
  },
  {
    key: 'remoteRealtimeTools',
    label: 'Remote Realtime Tools',
    blurb: 'MCP realtime source registration admin page.',
  },
  {
    key: 'platformCosts',
    label: 'Platform Costs',
    blurb: 'LLM cost, usage, and trace detail.',
  },
  {
    key: 'robotOnboarding',
    label: 'Robot Onboarding',
    blurb:
      'Built-in Robot onboarding agent (agt_robot_onboarding). Also needs Remote Realtime Tools enabled on the MCP server.',
  },
];

function FeatureFlagsCard({ orgId }: { orgId: string }) {
  const client = useClient();
  const qc = useQueryClient();
  const toast = useToast();

  const flags = useQuery({
    queryKey: ['admin', 'org-feature-flags', orgId],
    queryFn: () =>
      client.http.get(`v1/admin/orgs/${orgId}/feature-flags`).json<ConsoleFeatureFlags>(),
  });

  const save = useMutation({
    mutationFn: (patch: Partial<ConsoleFeatureFlags>) =>
      client.http
        .patch(`v1/admin/orgs/${orgId}/feature-flags`, { json: patch })
        .json<ConsoleFeatureFlags>(),
    onSuccess: async (updated) => {
      qc.setQueryData(['admin', 'org-feature-flags', orgId], updated);
      try {
        // If the superuser edited their own org, the rail re-derives too.
        await qc.invalidateQueries({ queryKey: ['org-settings', 'feature-flags'] });
      } catch (err) {
        console.error(err);
      }
      toast.show({ kind: 'success', text: 'Feature flags updated' });
    },
    onError: (err) => {
      toast.show({ kind: 'error', text: `Failed: ${(err as Error).message}` });
    },
  });

  return (
    <div className="card">
      <div className="card-header">
        <span className="t-mono-label">FEATURE FLAGS</span>
        <h3 className="t-h3">Console sections</h3>
      </div>
      <div className="card-body">
        <p className="t-secondary" style={{ margin: '0 0 var(--sp-2)' }}>
          Formant-internal gating of nav sections that are built but not customer-ready. Changes
          apply to this org&apos;s console on next load. Superuser-only.
        </p>
        {flags.error ? (
          <div className="alert alert--danger">
            Failed to load flags: {(flags.error as Error).message}
          </div>
        ) : null}
        {flagRows.map(({ key, label, blurb }) => (
          <div
            key={key}
            style={{
              display: 'grid',
              gridTemplateColumns: '220px 1fr auto',
              padding: 'var(--sp-2) 0',
              borderTop: '1px solid var(--border-subtle)',
              alignItems: 'center',
              gap: 'var(--sp-3)',
            }}
          >
            <span style={{ color: 'var(--text-primary)' }}>{label}</span>
            <span className="t-secondary">{blurb}</span>
            <input
              type="checkbox"
              className="switch"
              role="switch"
              aria-label={`${label} enabled`}
              checked={flags.data?.[key] ?? false}
              disabled={flags.isLoading || save.isPending}
              onChange={(e) => save.mutate({ [key]: e.target.checked })}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        padding: 'var(--sp-2) 0',
        borderTop: '1px solid var(--border-subtle)',
        alignItems: 'baseline',
        gap: 'var(--sp-3)',
      }}
    >
      <span className="t-secondary">{label}</span>
      <span style={{ color: 'var(--text-primary)' }}>{children}</span>
    </div>
  );
}

function InviteModal({
  open,
  onClose,
  onInvited,
  orgId,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
  orgId: string;
}) {
  const client = useClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const invite = useMutation({
    mutationFn: () =>
      inviteOrgMemberAdmin(client, orgId, { email, name: name.trim() || undefined }),
    onSuccess: () => {
      const invited = email;
      setEmail('');
      setName('');
      toast.show({ kind: 'success', text: `Invite sent to ${invited}` });
      onInvited();
    },
    onError: async (err) => {
      const lines = await readApiValidationErrors(err);
      toast.show({
        kind: 'error',
        text: lines.length > 0 ? lines.join(' · ') : (err as Error).message,
      });
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite user"
      eyebrow="ORG · MEMBERS"
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              if (email.trim()) invite.mutate();
            }}
            disabled={invite.isPending || !email.trim()}
          >
            {invite.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </>
      }
    >
      <form style={stack3} onSubmit={(e) => e.preventDefault()}>
        <label className="field">
          <span className="field-label">Email</span>
          <input
            type="email"
            required={true}
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">Display name (optional)</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {invite.error ? (
          <div className="alert alert--danger">{(invite.error as Error).message}</div>
        ) : null}
      </form>
    </Modal>
  );
}
